import { useRef, useState, useCallback, useLayoutEffect } from 'react';
import type { ReactNode } from 'react';
import { Menu, Tabs, UnstyledButton, Text } from '@mantine/core';
import { MoreHorizontal } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

export interface ModuleNavTab {
  value: string;
  label: string;
  icon: LucideIcon;
  rightSection?: ReactNode;
}

interface ModuleNavProps {
  tabs: ModuleNavTab[];
  activeTab: string;
  onChange: (value: string) => void;
}

/**
 * Pure fit calculation — how many leading tabs render inline before the rest
 * spill into the "More" menu. Exported so it can be unit-tested in jsdom, where
 * real layout (offsetWidth/clientWidth) is always 0 and the component path can't
 * exercise overflow on its own.
 *
 * Contract:
 *  - Everything fits (sum of tab widths <= container) → return tabs.length and
 *    NO room is reserved for a More button (there won't be one).
 *  - Otherwise a More button is required, so the budget shrinks by its width;
 *    count the leading tabs that fit in `containerWidth - moreButtonWidth`.
 *  - Empty list → 0. All-zero widths (jsdom / pre-measure) → everything "fits"
 *    (0 <= 0), so the deterministic default is: render ALL tabs inline, no More
 *    button. That keeps the component SSR/first-paint-safe and lets every
 *    existing consumer nav spec (which counts role="tab") pass unchanged.
 */
export function computeInlineTabCount(
  tabWidths: readonly number[],
  containerWidth: number,
  moreButtonWidth: number,
): number {
  const count = tabWidths.length;
  if (count === 0) return 0;

  let total = 0;
  for (const w of tabWidths) total += w;

  // Everything fits — no More button, full container available.
  if (total <= containerWidth) return count;

  // Overflow: reserve room for the persistent More trigger.
  const available = containerWidth - moreButtonWidth;
  let used = 0;
  let fit = 0;
  for (let i = 0; i < count; i++) {
    used += tabWidths[i];
    if (used <= available) fit++;
    else break;
  }
  return fit;
}

export function ModuleNav({ tabs, activeTab, onChange }: ModuleNavProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const moreMeasureRef = useRef<HTMLDivElement>(null);

  // SSR / first-paint safe: start with every tab inline. The layout effect
  // corrects this once real widths are known; under jsdom (all widths 0) the
  // pure fn returns tabs.length, so this initial value is also the final one.
  const [inlineCount, setInlineCount] = useState(tabs.length);

  const measure = useCallback(() => {
    const container = containerRef.current;
    const measureEl = measureRef.current;
    if (!container || !measureEl) return;

    const containerWidth = container.clientWidth;
    // Array.from — never spread a NodeList/iterable (Babel downlevel foot-gun).
    const tabEls = Array.from(measureEl.querySelectorAll<HTMLElement>('[role="tab"]'));
    const tabWidths = tabEls.map((el) => el.offsetWidth);
    const moreButtonWidth = moreMeasureRef.current?.offsetWidth ?? 0;

    setInlineCount(computeInlineTabCount(tabWidths, containerWidth, moreButtonWidth));
  }, []);

  // useLayoutEffect: measure before paint so we never flash the full row then
  // collapse. Atlas MF is client-only (no SSR), so this is safe here.
  useLayoutEffect(() => {
    measure();

    const container = containerRef.current;
    let ro: ResizeObserver | undefined;
    if (container && typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(() => measure());
      ro.observe(container);
    }

    // Debounced window-resize fallback for environments without ResizeObserver.
    let raf = 0;
    const onResize = () => {
      if (typeof cancelAnimationFrame !== 'undefined') cancelAnimationFrame(raf);
      raf =
        typeof requestAnimationFrame !== 'undefined'
          ? requestAnimationFrame(measure)
          : (setTimeout(measure, 100) as unknown as number);
    };
    window.addEventListener('resize', onResize);

    return () => {
      ro?.disconnect();
      window.removeEventListener('resize', onResize);
      if (typeof cancelAnimationFrame !== 'undefined') cancelAnimationFrame(raf);
    };
    // Re-measure when the tab set changes (labels/count affect widths).
  }, [measure, tabs]);

  // Clamp against the current list — inlineCount can be stale for one frame
  // right after `tabs` changes, before the layout effect re-runs.
  const safeInline = Math.min(inlineCount, tabs.length);
  const inlineTabs = tabs.slice(0, safeInline);
  const overflowTabs = tabs.slice(safeInline);
  const hasOverflow = overflowTabs.length > 0;
  const activeInOverflow = overflowTabs.some((t) => t.value === activeTab);

  return (
    <div
      ref={containerRef}
      data-nav-container
      style={{ display: 'flex', alignItems: 'center', width: '100%', minWidth: 0 }}
    >
      {/* Hidden measurement copy — natural per-tab widths + the More trigger
          width. aria-hidden keeps it out of the a11y tree, so getByRole('tab')
          and screen readers only see the real inline tabs. */}
      <div
        ref={measureRef}
        aria-hidden
        style={{
          position: 'absolute',
          visibility: 'hidden',
          height: 0,
          overflow: 'hidden',
          pointerEvents: 'none',
          left: 0,
          top: 0,
          whiteSpace: 'nowrap',
        }}
      >
        <Tabs value={activeTab}>
          <Tabs.List style={{ flexWrap: 'nowrap' }}>
            {tabs.map(({ value, label, icon: Icon, rightSection }) => (
              <Tabs.Tab
                key={value}
                value={value}
                leftSection={<Icon size={16} />}
                rightSection={rightSection}
                style={{ whiteSpace: 'nowrap' }}
              >
                {label}
              </Tabs.Tab>
            ))}
          </Tabs.List>
        </Tabs>
        {/* Sample of the More trigger so its width is reserved accurately. */}
        <div
          ref={moreMeasureRef}
          data-more-measure
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '0 12px', whiteSpace: 'nowrap' }}
        >
          <MoreHorizontal size={16} />
          <Text size="sm" fw={500}>
            More
          </Text>
        </div>
      </div>

      {/* Visible inline tabs — only the ones that fit. */}
      <div style={{ flex: '1 1 auto', minWidth: 0, overflow: 'hidden' }}>
        <Tabs value={activeTab} onChange={(v) => v && onChange(v)}>
          <Tabs.List style={{ flexWrap: 'nowrap' }}>
            {inlineTabs.map(({ value, label, icon: Icon, rightSection }) => (
              <Tabs.Tab
                key={value}
                value={value}
                leftSection={<Icon size={16} />}
                rightSection={rightSection}
                style={{ whiteSpace: 'nowrap' }}
              >
                {label}
              </Tabs.Tab>
            ))}
          </Tabs.List>
        </Tabs>
      </div>

      {/* Persistent trailing More menu — supplements the bar, never replaces it.
          Rendered only when at least one tab overflows (any viewport). */}
      {hasOverflow && (
        <div style={{ flexShrink: 0, marginLeft: 4 }}>
          <Menu shadow="md" width={220} position="bottom-end">
            <Menu.Target>
              <UnstyledButton
                aria-label="More navigation"
                aria-haspopup="menu"
                aria-current={activeInOverflow ? 'page' : undefined}
                px="sm"
                py="xs"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  whiteSpace: 'nowrap',
                  borderRadius: 'var(--mantine-radius-sm)',
                  color: activeInOverflow ? 'var(--mantine-color-red-6)' : undefined,
                }}
              >
                <MoreHorizontal size={16} />
                <Text size="sm" fw={activeInOverflow ? 600 : 500}>
                  More
                </Text>
              </UnstyledButton>
            </Menu.Target>

            <Menu.Dropdown>
              {overflowTabs.map(({ value, label, icon: Icon, rightSection }) => (
                <Menu.Item
                  key={value}
                  leftSection={<Icon size={16} />}
                  rightSection={rightSection}
                  onClick={() => onChange(value)}
                  aria-current={value === activeTab ? 'page' : undefined}
                  bg={value === activeTab ? 'var(--mantine-color-red-0)' : undefined}
                  c={value === activeTab ? 'red' : undefined}
                  fw={value === activeTab ? 500 : undefined}
                >
                  {label}
                </Menu.Item>
              ))}
            </Menu.Dropdown>
          </Menu>
        </div>
      )}
    </div>
  );
}
